import { stripMarkdown, recordKey } from './format';
import { areSameStory } from './storyGrouping';

const STORAGE_KEY = 'corpintel_watchlist_snapshots';

function loadSnapshots() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveSnapshots(map) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    // storage unavailable
  }
}

function fingerprint(record) {
  return {
    title: (record?.title || '').trim(),
    severity: record?.severity || '',
    summary: (record?.summary || '').trim(),
    snippet: stripMarkdown(record?.snippet || record?.content || '').slice(0, 300).trim(),
    published_at: record?.published_at || '',
  };
}

function snapshotRecord(snapshot, fallbackRecord) {
  return {
    id: fallbackRecord?.id,
    url: fallbackRecord?.url,
    company: fallbackRecord?.company || snapshot?.company || '',
    title: fallbackRecord?.title || snapshot?.fingerprint?.title || '',
    severity: fallbackRecord?.severity || snapshot?.fingerprint?.severity || '',
    summary: fallbackRecord?.summary || snapshot?.fingerprint?.summary || '',
    snippet: fallbackRecord?.snippet || snapshot?.fingerprint?.snippet || '',
    published_at: fallbackRecord?.published_at || snapshot?.fingerprint?.published_at || '',
  };
}

function fingerprintsMatch(a, b) {
  return JSON.stringify(a) === JSON.stringify(b);
}

export function saveWatchlistSnapshot(record) {
  const id = recordKey(record);
  if (!id) return;

  const snapshots = loadSnapshots();
  snapshots[id] = {
    fingerprint: fingerprint(record),
    company: record?.company || '',
    savedAt: snapshots[id]?.savedAt || new Date().toISOString(),
    acknowledgedAt: new Date().toISOString(),
  };
  saveSnapshots(snapshots);
}

export function removeWatchlistSnapshot(recordId) {
  if (!recordId) return;
  const snapshots = loadSnapshots();
  delete snapshots[recordId];
  saveSnapshots(snapshots);
}

/** Baseline missing snapshots so existing saves don't all flash as updated. */
export function ensureWatchlistSnapshot(record) {
  const id = recordKey(record);
  if (!id) return false;

  const snapshots = loadSnapshots();
  if (!snapshots[id]) {
    snapshots[id] = {
      fingerprint: fingerprint(record),
      company: record?.company || '',
      savedAt: new Date().toISOString(),
      acknowledgedAt: new Date().toISOString(),
    };
    saveSnapshots(snapshots);
    return false;
  }
  return !fingerprintsMatch(snapshots[id].fingerprint, fingerprint(record));
}

export function isWatchlistItemUpdated(record) {
  const id = recordKey(record);
  if (!id) return false;

  const snapshots = loadSnapshots();
  const snap = snapshots[id];
  if (!snap) return false;

  return !fingerprintsMatch(snap.fingerprint, fingerprint(record));
}

export function acknowledgeWatchlistItem(record) {
  const id = recordKey(record);
  if (!id) return;

  const snapshots = loadSnapshots();
  if (!snapshots[id]) {
    saveWatchlistSnapshot(record);
    return;
  }

  snapshots[id] = {
    ...snapshots[id],
    fingerprint: fingerprint(record),
    company: record?.company || snapshots[id].company || '',
    acknowledgedAt: new Date().toISOString(),
  };
  saveSnapshots(snapshots);
}

function hasSameStoryUpdate(record, allRecords, snapshot) {
  const id = recordKey(record);
  const baseline = snapshotRecord(snapshot, record);
  const acknowledgedAt = snapshot?.acknowledgedAt ? new Date(snapshot.acknowledgedAt).getTime() : 0;
  const savedAt = snapshot?.savedAt ? new Date(snapshot.savedAt).getTime() : 0;
  const cutoff = Math.max(acknowledgedAt, savedAt);

  return (allRecords || []).some(candidate => {
    const candidateId = recordKey(candidate);
    if (!candidateId || candidateId === id) return false;
    if (!areSameStory(baseline, candidate)) return false;

    const publishedAt = candidate.published_at ? new Date(candidate.published_at).getTime() : 0;
    const fetchedAt = candidate.fetched_at ? new Date(candidate.fetched_at).getTime() : 0;
    const candidateTime = Math.max(publishedAt, fetchedAt);
    return !cutoff || candidateTime > cutoff;
  });
}

export function getWatchlistItemUpdates(record, allRecords) {
  const id = recordKey(record);
  if (!id) return [];

  const snapshots = loadSnapshots();
  const snapshot = snapshots[id];
  if (!snapshot) return [];

  const baseline = snapshotRecord(snapshot, record);
  const acknowledgedAt = snapshot.acknowledgedAt ? new Date(snapshot.acknowledgedAt).getTime() : 0;
  const savedAt = snapshot.savedAt ? new Date(snapshot.savedAt).getTime() : 0;
  const cutoff = Math.max(acknowledgedAt, savedAt);

  return (allRecords || [])
    .filter(candidate => {
      const candidateId = recordKey(candidate);
      if (!candidateId || candidateId === id) return false;
      if (!areSameStory(baseline, candidate)) return false;

      const publishedAt = candidate.published_at ? new Date(candidate.published_at).getTime() : 0;
      const fetchedAt = candidate.fetched_at ? new Date(candidate.fetched_at).getTime() : 0;
      return !cutoff || Math.max(publishedAt, fetchedAt) > cutoff;
    })
    .sort((a, b) => new Date(b.published_at || b.fetched_at || 0) - new Date(a.published_at || a.fetched_at || 0));
}

export function countUpdatedWatchlistItems(records, watchlist) {
  if (!watchlist?.length) return 0;

  const snapshots = loadSnapshots();
  return records
    .filter(r => watchlist.includes(recordKey(r)))
    .filter(r => isWatchlistItemUpdated(r) || hasSameStoryUpdate(r, records, snapshots[recordKey(r)]))
    .length;
}

export function getUpdatedWatchlistIds(records, watchlist) {
  if (!watchlist?.length) return new Set();

  const snapshots = loadSnapshots();
  return new Set(
    records
      .filter(r => watchlist.includes(recordKey(r)))
      .filter(r => {
        ensureWatchlistSnapshot(r);
        return isWatchlistItemUpdated(r) || hasSameStoryUpdate(r, records, snapshots[recordKey(r)]);
      })
      .map(r => recordKey(r)),
  );
}
